import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listTransactions } from "../../lib/api-modules/transactions";
import { formatCentsCurrency } from "../../lib/util/money";
import {
  nyState529Deduction,
  type FilingStatus,
} from "../../lib/util/state-tax";
import { ErrorMessage } from "../auth/error-message";

/**
 * NY 529 deduction tracker — Phase 5 Session 5.
 *
 * Renders below a 529 account drilldown when the account's
 * `plan_state_code === "NY"`. Pulls the year's `529_contribution`
 * transactions, sums them, and feeds the sum into the pure
 * `nyState529Deduction` helper to surface deductible / cap / excess
 * tiles.
 *
 * The account-of-record vs. contributor caveat is explicit in the
 * banner text (only the NY filer who is the account owner gets the
 * deduction; spouses contributing through a non-NY-filing parent's
 * account don't).
 */

export function Ny529DeductionTile({
  accountId,
}: {
  readonly accountId: string;
}) {
  const currentYear = new Date().getUTCFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [filingStatus, setFilingStatus] = useState<FilingStatus>("mfj");

  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;

  const { data, isPending, isError, error } = useQuery({
    queryKey: [
      "accounts",
      accountId,
      "transactions",
      "529_contribution",
      year,
    ],
    queryFn: () =>
      listTransactions(accountId, {
        transaction_type: "529_contribution",
        from: yearStart,
        to: yearEnd,
      }),
  });

  const totalCents = useMemo(() => {
    if (!data?.transactions) return 0;
    return data.transactions.reduce((sum, row) => {
      const v = row["amount_cents"];
      return sum + (typeof v === "number" ? v : 0);
    }, 0);
  }, [data]);

  const result = useMemo(
    () => nyState529Deduction(totalCents, filingStatus),
    [totalCents, filingStatus],
  );

  const yearOptions = useMemo(() => {
    const out: number[] = [];
    for (let y = currentYear; y >= currentYear - 5; y -= 1) out.push(y);
    return out;
  }, [currentYear]);

  return (
    <section
      aria-labelledby="ny529-heading"
      className="mt-6 rounded-md border border-zinc-200 p-4 dark:border-zinc-800"
    >
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 id="ny529-heading" className="text-base font-semibold">
          NY 529 deduction — tax year {year}
        </h3>
        <div className="flex items-center gap-2 text-sm">
          <label className="flex items-center gap-1">
            <span className="text-zinc-500">Tax year</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-1">
            <span className="text-zinc-500">Filing status</span>
            <select
              value={filingStatus}
              onChange={(e) => setFilingStatus(e.target.value as FilingStatus)}
              className="rounded-md border border-zinc-300 px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
            >
              <option value="mfj">Married filing jointly</option>
              <option value="single">Single / MFS / HoH</option>
            </select>
          </label>
        </div>
      </header>

      {isPending && (
        <p className="text-sm text-zinc-500">Loading contributions…</p>
      )}
      {isError && (
        <ErrorMessage>
          Couldn't load contributions: {error instanceof Error ? error.message : "unknown"}
        </ErrorMessage>
      )}

      {!isPending && !isError && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Tile label="Contributions YTD" value={formatCentsCurrency(totalCents)} />
            <Tile
              label="Cap"
              value={formatCentsCurrency(result.cap_cents)}
            />
            <Tile
              label="Deductible"
              value={formatCentsCurrency(result.deductible_cents)}
              tone="positive"
            />
            <Tile
              label="Over cap"
              value={formatCentsCurrency(result.excess_cents)}
              tone={result.excess_cents > 0 ? "warning" : "muted"}
            />
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            Deduction shown assumes the NY filer is the account owner-of-record.
            Grandparents or non-NY filers contributing to this account do <em>not</em>
            get the NY deduction on their own return — see{" "}
            <a
              href="https://www.tax.ny.gov/forms/income_cur_forms.htm"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              NY IT-201-D instructions
            </a>
            .
          </p>
          <p className="mt-1 text-xs text-zinc-500">
            {data?.transactions?.length ?? 0} contribution{data?.transactions?.length === 1 ? "" : "s"} recorded for {year}.
          </p>
        </>
      )}
    </section>
  );
}

function Tile({
  label,
  value,
  tone = "neutral",
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: "neutral" | "positive" | "warning" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-emerald-700 dark:text-emerald-300"
      : tone === "warning"
        ? "text-amber-700 dark:text-amber-300"
        : tone === "muted"
          ? "text-zinc-500"
          : "";
  return (
    <div className="rounded-md border border-zinc-200 p-3 dark:border-zinc-800">
      <p className="text-xs text-zinc-500">{label}</p>
      <p className={`mt-1 text-lg font-medium tabular-nums ${toneClass}`}>{value}</p>
    </div>
  );
}
